import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  DatabaseBackup,
  Download,
  HardDrive,
  History,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CompactStatCard,
  DataTable,
  FormInput,
  FormSelect,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  useBackups,
  useCreateBackupMutation,
  useDeleteBackupMutation,
  useUpdateBackupSettingsMutation,
} from "@/hooks/queries";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import { backupService } from "@/services/backupService";
import type { BackupFileDto, BackupFrequency, BackupSettingsDto, BackupSettingsUpdateDto } from "@/types/backup.dto";

const SEARCH_KEYS: (keyof BackupFileDto)[] = ["name", "type"];

const frequencyOptions = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];
const weekdayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((label, index) => ({
  label,
  value: String(index),
}));
const monthDayOptions = Array.from({ length: 28 }, (_, i) => ({ label: `Day ${i + 1}`, value: String(i + 1) }));

const typeBadge: Record<string, string> = {
  manual: "bg-info/10 text-info border-info/20",
  auto: "bg-primary/10 text-primary border-primary/20",
  other: "",
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
};

const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/** "02:00" UTC → "07:30 IST" for the admins who think in local time. */
const toIst = (utc: string) => {
  const [h, m] = utc.split(":").map(Number);
  const total = (h * 60 + m + 330) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const scheduleSummary = (s: BackupSettingsDto) => {
  const when = `${s.timeOfDay} UTC (${toIst(s.timeOfDay)} IST)`;
  const day =
    s.frequency === "weekly" ? ` on ${weekdayOptions[s.dayOfWeek]?.label}` : s.frequency === "monthly" ? ` on day ${s.dayOfMonth}` : "";
  return `${s.frequency[0].toUpperCase()}${s.frequency.slice(1)}${day} at ${when} · keep ${s.retentionDays} days`;
};

const toForm = (s: BackupSettingsDto): BackupSettingsUpdateDto => ({
  enabled: s.enabled,
  frequency: s.frequency,
  timeOfDay: s.timeOfDay,
  dayOfWeek: s.dayOfWeek,
  dayOfMonth: s.dayOfMonth,
  retentionDays: s.retentionDays,
});

const BackupSettingsSection = () => {
  const { toast } = useToast();
  const { data, isLoading, isError, refetch } = useBackups();
  const createMutation = useCreateBackupMutation();
  const updateMutation = useUpdateBackupSettingsMutation();
  const deleteMutation = useDeleteBackupMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState<BackupSettingsUpdateDto | null>(null);
  const [deleting, setDeleting] = useState<BackupFileDto | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const backups = data?.backups ?? [];
  const table = useDataTable<BackupFileDto>({ data: backups, searchKeys: SEARCH_KEYS });

  // Keep the form in sync if the sheet is open while data reloads for the first time.
  useEffect(() => {
    if (sheetOpen && !form && data) setForm(toForm(data.settings));
  }, [sheetOpen, form, data]);

  const totalSize = useMemo(() => backups.reduce((sum, b) => sum + b.sizeBytes, 0), [backups]);
  const running = createMutation.isPending || Boolean(data?.isRunning);

  const handleBackupNow = async () => {
    try {
      const created = await createMutation.mutateAsync();
      toast({ title: "Backup created", description: `${created.name} (${formatBytes(created.sizeBytes)})` });
    } catch (error) {
      toast({ title: "Backup failed", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDownload = async (file: BackupFileDto) => {
    setDownloading(file.name);
    try {
      await backupService.download(file.name);
    } catch (error) {
      toast({ title: "Download failed", description: getApiErrorMessage(error), variant: "destructive" });
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.name);
      toast({ title: "Backup deleted" });
    } catch (error) {
      toast({ title: "Could not delete backup", description: getApiErrorMessage(error), variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const openSchedule = () => {
    if (!data) return;
    setForm(toForm(data.settings));
    setSheetOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!form) return;
    try {
      await updateMutation.mutateAsync(form);
      toast({ title: "Backup schedule saved" });
      setSheetOpen(false);
    } catch (error) {
      toast({ title: "Could not save schedule", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const columns: DataTableColumn<BackupFileDto>[] = [
    {
      id: "name",
      header: "Backup",
      sortable: true,
      mobilePriority: 1,
      cell: (row) => <span className="break-all font-mono text-xs font-medium">{row.name}</span>,
    },
    {
      id: "type",
      header: "Type",
      sortable: true,
      mobileLabel: "Type",
      mobilePriority: 2,
      cell: (row) => (
        <Badge variant="outline" className={`capitalize ${typeBadge[row.type] ?? ""}`}>
          {row.type === "auto" ? "Scheduled" : row.type}
        </Badge>
      ),
    },
    {
      id: "sizeBytes",
      header: "Size",
      sortable: true,
      align: "right",
      mobileLabel: "Size",
      mobilePriority: 3,
      cell: (row) => formatBytes(row.sizeBytes),
    },
    {
      id: "createdAt",
      header: "Created",
      sortable: true,
      mobileLabel: "Created",
      mobilePriority: 4,
      className: "text-sm text-muted-foreground",
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      hideOnMobile: true,
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Download"
            disabled={downloading === row.name}
            onClick={() => handleDownload(row)}
          >
            {downloading === row.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete" onClick={() => setDeleting(row)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
        <p className="text-sm text-destructive">Could not load backups.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const { settings } = data;
  const latest = backups[0];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Database Backup"
        description="Back up the database on demand or on a schedule. Backups are stored on the server in app_data/backup."
        actions={
          <Button className="gap-2" onClick={handleBackupNow} disabled={running || !data.pgDumpAvailable}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}
            {running ? "Backing up…" : "Backup now"}
          </Button>
        }
      />

      {!data.pgDumpAvailable && (
        <Alert variant="destructive" className="py-2.5">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <code>pg_dump</code> is not installed on the server, so backups cannot be created. Rebuild the API image
            (the Dockerfile installs the Postgres client) or install PostgreSQL client tools on the server.
          </AlertDescription>
        </Alert>
      )}

      <StatsGrid columns={4}>
        <CompactStatCard title="Backups" value={backups.length} icon={History} />
        <CompactStatCard title="Total size" value={formatBytes(totalSize)} icon={HardDrive} />
        <CompactStatCard title="Last backup" value={latest ? formatDateTime(latest.createdAt) : "None yet"} icon={CheckCircle2} />
        <CompactStatCard
          title="Next scheduled"
          value={settings.enabled && data.nextRunAt ? formatDateTime(data.nextRunAt) : "Off"}
          icon={Clock}
          iconClassName="bg-gradient-primary"
        />
      </StatsGrid>

      <div className="glass-card flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <CalendarClock className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 basis-56">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold">Automatic backups</p>
            <Badge variant={settings.enabled ? "default" : "secondary"} className="px-1.5 py-0 text-[10px]">
              {settings.enabled ? "On" : "Off"}
            </Badge>
          </div>
          <p className="truncate text-xs text-muted-foreground">{scheduleSummary(settings)}</p>
          {settings.lastAutoRunAt && (
            <p className={`truncate text-[11px] ${settings.lastRunStatus === "failed" ? "text-destructive" : "text-muted-foreground"}`}>
              Last scheduled run {formatDateTime(settings.lastAutoRunAt)} —{" "}
              {settings.lastRunStatus === "failed" ? `failed${settings.lastError ? `: ${settings.lastError}` : ""}` : "succeeded"}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={openSchedule}>
          <Pencil className="h-3.5 w-3.5" /> Edit schedule
        </Button>
      </div>

      <DataTable
        title={`Backups (${backups.length})`}
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.name}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search backups…"
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No backups yet. Use “Backup now” or turn on automatic backups."
        renderMobileCard={(row) => (
          <div className="space-y-3 rounded-lg border border-border p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="break-all font-mono text-xs font-medium">{row.name}</p>
              <Badge variant="outline" className={`shrink-0 capitalize ${typeBadge[row.type] ?? ""}`}>
                {row.type === "auto" ? "Scheduled" : row.type}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatBytes(row.sizeBytes)} · {formatDateTime(row.createdAt)}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" disabled={downloading === row.name} onClick={() => handleDownload(row)}>
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download
              </Button>
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => setDeleting(row)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>Automatic backups</SheetTitle>
            <SheetDescription>Schedule a recurring database backup. Scheduled backups run on the server, no one needs to be logged in.</SheetDescription>
          </SheetHeader>

          {form && (
            <>
              <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="backup-enabled">Enable automatic backups</Label>
                  <Switch id="backup-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                </div>

                <FormSelect
                  label="Frequency"
                  value={form.frequency}
                  onValueChange={(v) => setForm({ ...form, frequency: v as BackupFrequency })}
                  options={frequencyOptions}
                />

                {form.frequency === "weekly" && (
                  <FormSelect
                    label="Day of week"
                    value={String(form.dayOfWeek)}
                    onValueChange={(v) => setForm({ ...form, dayOfWeek: Number(v) })}
                    options={weekdayOptions}
                  />
                )}
                {form.frequency === "monthly" && (
                  <FormSelect
                    label="Day of month"
                    value={String(form.dayOfMonth)}
                    onValueChange={(v) => setForm({ ...form, dayOfMonth: Number(v) })}
                    options={monthDayOptions}
                    hint="Limited to 1–28 so it exists in every month."
                  />
                )}

                <FormInput
                  label="Time (UTC)"
                  type="time"
                  value={form.timeOfDay}
                  onChange={(e) => setForm({ ...form, timeOfDay: e.target.value })}
                  hint={form.timeOfDay ? `${form.timeOfDay} UTC is ${toIst(form.timeOfDay)} IST. Pick a quiet hour.` : undefined}
                />

                <FormInput
                  label="Keep scheduled backups for (days)"
                  type="number"
                  min={1}
                  max={365}
                  value={String(form.retentionDays)}
                  onChange={(e) => setForm({ ...form, retentionDays: Number(e.target.value) })}
                  hint="Older scheduled backups are deleted automatically. The newest one is always kept, and manual backups are never auto-deleted."
                />
              </div>

              <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
                <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleSaveSchedule}
                  disabled={
                    updateMutation.isPending ||
                    !form.timeOfDay ||
                    !Number.isInteger(form.retentionDays) ||
                    form.retentionDays < 1 ||
                    form.retentionDays > 365
                  }
                >
                  {updateMutation.isPending ? "Saving…" : "Save schedule"}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="break-all font-mono text-xs">{deleting?.name}</span> will be removed from the server. This
              can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete}>
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupSettingsSection;
